import { z } from 'zod';
import { DomainError } from '../errors.js';

/**
 * Platform klasör şablonu (ADR 0037 §1–§3, §8; ADR 0038 §4). Şablon `felix_control`'dedir,
 * sürümlüdür ve yayınlanmış sürüm değişmez; düğümlerin kalıcı anahtarı vardır. Bu modül saftır:
 * worker komutu, A1 paneli, bulut ve gemi aynı şemayı ve kuralları kullanır. Kayıt silinmez,
 * klasör yalnız emekli olur [NR496 4.3.2a].
 */

/** Şablonun bölümleri: ISM Forms ve Manuals (ADR 0037 §8). */
export const TEMPLATE_SECTIONS = ['ism', 'manuals'] as const;
export type TemplateSection = (typeof TEMPLATE_SECTIONS)[number];

/** Bir sürümde en çok düğüm ve düzey (kök 1. düzeydir) (ADR 0038 §4). */
export const MAX_TEMPLATE_NODES = 500;
export const MAX_TEMPLATE_DEPTH = 6;

/**
 * Anahtar biçimi (ADR 0038 §4): ilk parça bölümdür, ardından 1–5 parça; en çok 120 karakter.
 * Anahtar düğümün yerini anlatmaz; düğüm taşınsa da anahtar aynı kalır (ADR 0037 §3).
 */
export const NODE_KEY_PATTERN = /^(ism|manuals)(\.[a-z0-9][a-z0-9-]{0,47}){1,5}$/;
export const MAX_NODE_KEY_LENGTH = 120;

/** Ofise ayrılmış parça öneki: ofisin eklediği klasör `<bölüm>.office-<uuidv7>` (ADR 0038 §4). */
export const OFFICE_KEY_PREFIX = 'office-';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

const text = (min: number, max: number) => z.string().trim().min(min).max(max);

/** Şablon düğümü. Anahtar biçimi şemada değil `validateTemplateTree`'de denetlenir (hata kodu). */
export const templateNodeSchema = z.strictObject({
  key: z.string(),
  section: z.enum(TEMPLATE_SECTIONS),
  parentKey: z.string().nullable(),
  order: z.number().int().min(0).max(1_000_000),
  code: text(1, 32),
  name: z.strictObject({ en: text(1, 120), tr: text(1, 120) }),
});
export type TemplateNode = z.infer<typeof templateNodeSchema>;

const nodesSchema = z.array(templateNodeSchema).max(MAX_TEMPLATE_NODES);

/** Bir sürümün ağacı; `doc_folder_template_versions.tree` bu biçimdedir (ADR 0038 §4). */
export const templateTreeSchema = z.strictObject({ nodes: nodesSchema });
export type TemplateTree = z.infer<typeof templateTreeSchema>;

/**
 * Yayın dosyası (`doc-template publish --file`; `infra/doc-templates/*.json`). Not dosyada ya da
 * `--note` ile verilir; ikisinden biri zorunludur (worker denetler).
 */
export const templateFileSchema = z.strictObject({
  template: text(1, 80),
  description: text(1, 500).optional(),
  note: text(1, 2000).optional(),
  nodes: nodesSchema,
});
export type TemplateFile = z.infer<typeof templateFileSchema>;

/** Ofis düğümü anahtarı mı (`<bölüm>.office-<uuid>`). */
export function isOfficeNodeKey(key: string): boolean {
  const [section = '', part = '', ...rest] = key.split('.');
  return (
    rest.length === 0 &&
    (TEMPLATE_SECTIONS as readonly string[]).includes(section) &&
    part.startsWith(OFFICE_KEY_PREFIX) &&
    UUID_PATTERN.test(part.slice(OFFICE_KEY_PREFIX.length))
  );
}

/**
 * Ofisin eklediği klasörün anahtarı (ADR 0038 §4, §8): aynı işlemle eklendiği bütün gemilerde
 * aynıdır, bu yüzden çağıran UUID'yi işlem başına bir kez üretir.
 */
export function officeNodeKey(section: TemplateSection, uuid: string): string {
  const key = `${section}.${OFFICE_KEY_PREFIX}${uuid}`;
  if (!isOfficeNodeKey(key)) throw new DomainError('INVALID_KEY', key, key);
  return key;
}

/**
 * Şablon düğümünün anahtarı (ADR 0038 §4): biçim ve uzunluk (`INVALID_KEY`), ilk parça bölüm
 * (`KEY_SECTION_PREFIX`), `office-` ile başlayan parça yok (`KEY_RESERVED`).
 */
export function assertTemplateKey(key: string, section: TemplateSection): void {
  if (key.length > MAX_NODE_KEY_LENGTH || !NODE_KEY_PATTERN.test(key)) {
    throw new DomainError('INVALID_KEY', key, key);
  }
  const [prefix, ...parts] = key.split('.');
  if (prefix !== section) throw new DomainError('KEY_SECTION_PREFIX', key, key);
  if (parts.some((part) => part.startsWith(OFFICE_KEY_PREFIX))) {
    throw new DomainError('KEY_RESERVED', key, key);
  }
}

/** Kardeş kodu karşılaştırması: kırpılmış, büyük-küçük harf duyarsız (M8a1 ile aynı). */
export function folderCodeKey(code: string): string {
  return code.trim().toLocaleLowerCase('en');
}

/** Kardeş sırası: `order`, sonra kod, sonra anahtar. Yerelden bağımsızdır (içerik hash'i). */
function compareSiblings(a: TemplateNode, b: TemplateNode): number {
  if (a.order !== b.order) return a.order - b.order;
  if (a.code !== b.code) return a.code < b.code ? -1 : 1;
  return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
}

/** Bir şablon sürümü; `history` için. */
export interface TemplateVersionTree {
  version: number;
  tree: TemplateTree;
}

/**
 * Yeni sürümün karşılaştırıldığı geçmiş (ADR 0038 §4): aynı şablonun önceki sürümleri (`own`)
 * ve aynı anahtarları taşıyan diğer şablonların sürümleri (`others`).
 */
export interface TemplateHistory {
  own: readonly TemplateVersionTree[];
  others: readonly TemplateVersionTree[];
}

const EMPTY_HISTORY: TemplateHistory = { own: [], others: [] };

/**
 * Şablon sürümünü doğrular ve kanonik sıraya dizer (ADR 0037 §2, §3; ADR 0038 §4):
 * - şema: kod 1–32, ad en/tr 1–120, en çok 500 düğüm (`VALIDATION_FAILED`);
 * - anahtar kuralları (`INVALID_KEY`, `KEY_SECTION_PREFIX`, `KEY_RESERVED`), sürümde tekil
 *   anahtar (`DUPLICATE_KEY`);
 * - üst anahtar aynı sürümde (`PARENT_NOT_FOUND`) ve aynı bölümde (`FOLDER_SECTION_MISMATCH`),
 *   döngü yok (`FOLDER_CYCLE`), en çok 6 düzey (`VALIDATION_FAILED`);
 * - kardeşler arasında kod tekil (`DUPLICATE_FOLDER_CODE`);
 * - şablonun önceki bir sürümünde olup son sürümünden çıkan anahtar geri gelemez (`KEY_REUSED`)
 *   [sorular #155]; aynı anahtar başka şablonda başka bölümde olamaz (`KEY_SECTION_MISMATCH`).
 *
 * Dönüş kanonik sıradadır: bölüm sırası, sonra üstten alta (önce üst, sonra altları), kardeşler
 * `order`, kod ve anahtara göre. Aynı ağaç her girdi sırasında aynı sonucu (ve içerik hash'ini)
 * verir; üst her zaman altından önce gelir.
 */
export function validateTemplateTree(
  next: unknown,
  history: TemplateHistory = EMPTY_HISTORY,
): TemplateTree {
  const parsed = templateTreeSchema.safeParse(next);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    throw new DomainError(
      'VALIDATION_FAILED',
      `template tree: ${issue?.path.map(String).join('.') ?? ''} ${issue?.message ?? ''}`,
      issue?.path.map(String).join('.'),
    );
  }
  const nodes = parsed.data.nodes;

  const byKey = new Map<string, TemplateNode>();
  for (const node of nodes) {
    assertTemplateKey(node.key, node.section);
    if (byKey.has(node.key)) throw new DomainError('DUPLICATE_KEY', node.key, node.key);
    byKey.set(node.key, node);
  }

  for (const node of nodes) {
    if (node.parentKey === null) continue;
    const parent = byKey.get(node.parentKey);
    if (!parent) throw new DomainError('PARENT_NOT_FOUND', node.parentKey, node.key);
    if (parent.section !== node.section) {
      throw new DomainError('FOLDER_SECTION_MISMATCH', node.key, node.key);
    }
  }

  // Döngü ve derinlik: her düğümden köke yürü
  for (const node of nodes) {
    const seen = new Set<string>([node.key]);
    let depth = 1;
    let cursor = node.parentKey;
    while (cursor !== null) {
      if (seen.has(cursor)) throw new DomainError('FOLDER_CYCLE', node.key, node.key);
      seen.add(cursor);
      depth += 1;
      cursor = byKey.get(cursor)?.parentKey ?? null;
    }
    if (depth > MAX_TEMPLATE_DEPTH) {
      throw new DomainError('VALIDATION_FAILED', `depth ${depth}: ${node.key}`, 'depth');
    }
  }

  const siblingCodes = new Set<string>();
  for (const node of nodes) {
    const slot = `${node.section}\u0000${node.parentKey ?? ''}\u0000${folderCodeKey(node.code)}`;
    if (siblingCodes.has(slot)) {
      throw new DomainError('DUPLICATE_FOLDER_CODE', node.code, node.key);
    }
    siblingCodes.add(slot);
  }

  assertHistory(byKey, history);

  return { nodes: canonicalOrder(nodes) };
}

/** Geçmişe karşı anahtar kuralları [sorular #155]. */
function assertHistory(byKey: ReadonlyMap<string, TemplateNode>, history: TemplateHistory): void {
  const latest = history.own.reduce<TemplateVersionTree | undefined>(
    (best, entry) => (best === undefined || entry.version > best.version ? entry : best),
    undefined,
  );
  if (latest) {
    const current = new Set(latest.tree.nodes.map((n) => n.key));
    for (const entry of history.own) {
      for (const old of entry.tree.nodes) {
        if (!current.has(old.key) && byKey.has(old.key)) {
          throw new DomainError('KEY_REUSED', old.key, old.key);
        }
      }
    }
  }
  // Ön ek kuralı yeni yayında bunu önler; geçmişteki bozuk ya da elle yazılmış sürüme karşı savunma
  for (const entry of [...history.own, ...history.others]) {
    for (const old of entry.tree.nodes) {
      const mine = byKey.get(old.key);
      if (mine && mine.section !== old.section) {
        throw new DomainError('KEY_SECTION_MISMATCH', old.key, old.key);
      }
    }
  }
}

/** Kanonik sıra: bölüm sırası, üstten alta, kardeşler `compareSiblings` ile. */
function canonicalOrder(nodes: readonly TemplateNode[]): TemplateNode[] {
  const children = new Map<string | null, TemplateNode[]>();
  for (const node of nodes) {
    const list = children.get(node.parentKey) ?? [];
    list.push(node);
    children.set(node.parentKey, list);
  }
  for (const list of children.values()) list.sort(compareSiblings);
  const ordered: TemplateNode[] = [];
  const visit = (node: TemplateNode) => {
    ordered.push(node);
    for (const child of children.get(node.key) ?? []) visit(child);
  };
  for (const section of TEMPLATE_SECTIONS) {
    for (const root of children.get(null) ?? []) {
      if (root.section === section) visit(root);
    }
  }
  return ordered;
}

/** İki sürüm arasındaki fark (A1 önizlemesi ve yayın özeti). */
export interface TemplateDiff {
  added: TemplateNode[];
  removed: TemplateNode[];
  /** Kodu ya da adı (en veya tr) değişen düğümler. */
  renamed: {
    key: string;
    before: Pick<TemplateNode, 'code' | 'name'>;
    after: Pick<TemplateNode, 'code' | 'name'>;
  }[];
  reordered: { key: string; before: number; after: number }[];
  /** Üst anahtarı değişen düğümler. */
  moved: { key: string; before: string | null; after: string | null }[];
}

/**
 * İki sürümü anahtara göre karşılaştırır. Bir düğüm birden çok listede olabilir (ör. hem
 * taşınmış hem adı değişmiş). Liste sırası: eklenen ve değişenler `next`'in, kaldırılanlar
 * `prev`'in sırasıyla.
 */
export function diffTemplateTrees(prev: TemplateTree, next: TemplateTree): TemplateDiff {
  const before = new Map(prev.nodes.map((n) => [n.key, n]));
  const after = new Set(next.nodes.map((n) => n.key));
  const diff: TemplateDiff = { added: [], removed: [], renamed: [], reordered: [], moved: [] };
  for (const node of next.nodes) {
    const old = before.get(node.key);
    if (!old) {
      diff.added.push(node);
      continue;
    }
    if (old.code !== node.code || old.name.en !== node.name.en || old.name.tr !== node.name.tr) {
      diff.renamed.push({
        key: node.key,
        before: { code: old.code, name: old.name },
        after: { code: node.code, name: node.name },
      });
    }
    if (old.order !== node.order) {
      diff.reordered.push({ key: node.key, before: old.order, after: node.order });
    }
    if (old.parentKey !== node.parentKey) {
      diff.moved.push({ key: node.key, before: old.parentKey, after: node.parentKey });
    }
  }
  for (const node of prev.nodes) {
    if (!after.has(node.key)) diff.removed.push(node);
  }
  return diff;
}

/** Varsayılan şablonun adı (ADR 0037 §4). */
export const STANDARD_TEMPLATE_NAME = 'Standard';

const root = (
  section: TemplateSection,
  key: string,
  order: number,
  en: string,
  tr: string,
): TemplateNode => ({
  key,
  section,
  parentKey: null,
  order,
  code: String(order).padStart(2, '0'),
  name: { en, tr },
});

/**
 * Standard v1 (ADR 0037 §8; ADR 0038 §4). Kodlar M8a1'in varsayılan klasörleriyle aynıdır; var
 * olan şirket klasörleri bölüm ve kökteki koda göre bu anahtarlara eşlenir (ADR 0038 §15). Alt
 * başlıkları platform yöneticisi sonraki sürümlerde ekler. `infra/doc-templates/standard-v1.json`
 * bu sabitten üretilir; düğümler kanonik sıradadır.
 */
export const STANDARD_V1: TemplateFile & { template: typeof STANDARD_TEMPLATE_NAME } = {
  template: STANDARD_TEMPLATE_NAME,
  description: 'Felixmate standard ISM Forms and Manuals folders',
  note: 'Standard v1: ISM 01–07 and Manuals 01–05 (ADR 0037 §8)',
  nodes: [
    root('ism', 'ism.01-sms', 1, 'Safety Management System', 'Emniyet Yönetim Sistemi'),
    root('ism', 'ism.02-crew', 2, 'Crew Management', 'Personel Yönetimi'),
    root('ism', 'ism.03-operations', 3, 'Operations', 'Operasyon'),
    root('ism', 'ism.04-maintenance', 4, 'Maintenance & PMS', 'Bakım ve PMS'),
    root('ism', 'ism.05-hse', 5, 'HSE', 'Sağlık, Emniyet ve Çevre'),
    root('ism', 'ism.06-purchasing', 6, 'Purchasing', 'Satın Alma'),
    root('ism', 'ism.07-forms', 7, 'ISM Forms', 'ISM Formları'),
    root('manuals', 'manuals.sms', 1, 'SMS Manual', 'SMS El Kitabı'),
    root('manuals', 'manuals.procedures', 2, 'Procedures Manual', 'Prosedürler El Kitabı'),
    root(
      'manuals',
      'manuals.ship-specific',
      3,
      'Ship Specific Manuals',
      'Gemiye Özgü El Kitapları',
    ),
    root('manuals', 'manuals.maker', 4, 'Maker Manuals', 'Üretici El Kitapları'),
    root('manuals', 'manuals.circulars', 5, 'Circulars', 'Sirkülerler'),
  ],
};
